/**
 * `/$tenantSlug/settings/lists` — the ONE managed-list surface.
 *
 * HF-W0b-07. The page itself is `components/ListsSettings.tsx`, imported on
 * demand: this file holds only the route and its permission gate, so a visitor
 * who never opens Settings → Lists never downloads it (ADR-030 §3.1, the same
 * split `settings.security.tsx` makes for its QR-code dependency).
 */

import { RequirePermission } from "@ken/auth";
import { Skeleton } from "@ken/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, lazy, type ReactElement } from "react";

const ListsSettingsContent = lazy(
  async () => import("../components/ListsSettings"),
);

export const Route = createFileRoute("/$tenantSlug/settings/lists")({
  component: ListsSettingsRoute,
});

/**
 * Hard permission gate (defense-in-depth), the same one Settings → Modules
 * carries: this is a tenant-settings admin surface, so reaching it by URL
 * without `shell.settings.view` renders the accessible no-access region rather
 * than the page. The three write buttons additionally need
 * `shell.settings.edit`, which is what the API enforces — no new permission was
 * invented for this screen, because renaming a list value is the same class of
 * act as turning a module on.
 *
 * The gate is OUTSIDE the lazy boundary on purpose: somebody who may not open
 * this page should not download it to be told so.
 */
function ListsSettingsRoute(): ReactElement {
  return (
    <RequirePermission slug="shell.settings.view">
      <Suspense fallback={<Skeleton className="h-40 w-full" />}>
        <ListsSettingsContent />
      </Suspense>
    </RequirePermission>
  );
}
